// @flow
import * as constants from './constants';
import request from 'db-request';
import {
  logoff as logoffAndRedirect,
  redirectToLandingPage,
} from '../utils/logoff';
import translate from '@e4/intl/translate.macro';
import { ButtonTypes } from '@e4/ui';

let isWarningMessageOpened = false;
let currentSessionId = null;
const SECURITY_COOKIE_NAME = 'NSSID';
const eventsForInactivityReset = [
  'mousedown',
  'keypress',
  'scroll',
  'touchstart',
  'wheel',
];
// Used for recurring session extend calls.
let activeWithinTimeframe = false;
const BACKGROUND_EXTEND_TIMEFRAME = 30000; // 30s

export function isUserAutoLoggedOff(enableTimer, sessionId) {
  const inactivityThreshold = window.localStorage.getItem(
    constants.THRESHOLD_PROP
  );

  return inactivityThreshold === constants.THRESHOLD.HIGH;
}

export function toggleInactivityTimer(enableTimer, sessionId) {
  currentSessionId = sessionId;

  if (enableTimer) {
    const inactivityStartedAt = window.localStorage.getItem(
      constants.INACTIVITY_START_PROP
    );

    //Do not reset timer if it is already set
    if (inactivityStartedAt) {
      return;
    }

    setDefaultLocalStorageValues();
  } else {
    clear();
  }
}

function recurringKeepAlive() {
  setTimeout(async () => {
    if (activeWithinTimeframe) {
      activeWithinTimeframe = false;
      await extendSession();
      resetOldEbankInactivityStart();
    }
    recurringKeepAlive();
  }, BACKGROUND_EXTEND_TIMEFRAME);
}

export function addInactivityListeners(callback) {
  window.addEventListener(
    'load',
    () => {
      clear();
      initializeContinuesInactivityCheck(callback);
      toggleInactivityTimer(true);
      recurringKeepAlive();
    },
    true
  );

  window.addEventListener('storage', (event) => {
    if (event.key !== constants.THRESHOLD_PROP) {
      return;
    }

    if (event.newValue === constants.THRESHOLD.LOW) {
      isWarningMessageOpened = false;
      callback();
    } else if (event.newValue === constants.THRESHOLD.MEDIUM) {
      isWarningMessageOpened = true;
      showWarningMessage(callback);
    } else if (event.newValue === constants.THRESHOLD.HIGH) {
      isWarningMessageOpened = false;
      redirectToLandingPage();
    }
  });

  eventsForInactivityReset.forEach(function (name) {
    window.addEventListener(name, resetInactivityStart, true);
  });
}

export function removeInactivityListeners(callback) {
  eventsForInactivityReset.forEach(function (name) {
    window.removeEventListener(name, resetInactivityStart);
  });
}

export function clear() {
  window.localStorage.removeItem(constants.THRESHOLD_PROP);
  window.localStorage.removeItem(constants.INACTIVITY_START_PROP);
}

function setDefaultLocalStorageValues() {
  window.localStorage.removeItem(constants.THRESHOLD_PROP);
  window.localStorage.setItem(constants.INACTIVITY_START_PROP, Date.now());

  resetOldEbankInactivityStart();
}

function resetInactivityStart() {
  if (isWarningMessageOpened) {
    return;
  }

  const inactivityStartedAt = window.localStorage.getItem(
    constants.INACTIVITY_START_PROP
  );

  // Set "activeWithinTimeframe" to true for recurring background keep-alives
  activeWithinTimeframe = true;

  //Re-initiate timer only if user is logged in
  if (inactivityStartedAt) {
    setDefaultLocalStorageValues();
  }
}

async function extendSession(toggleInactivityDialog) {
  try {
    await request.get('/security/auth/api/auth/v1/session/keep-alive', {
      baseUrl: process.env.REACT_APP_API_MARKET_BASE_URL,
    });
  } catch (ex) {
    // Silent
  }

  window.localStorage.setItem(constants.INACTIVITY_START_PROP, Date.now());

  if (toggleInactivityDialog) {
    toggleInactivityDialog();

    isWarningMessageOpened = false;
  }

  notifyOtherTabsOnInactivityStatus(constants.THRESHOLD.LOW);
}

async function logOff(toggleInactivityDialog) {
  const callback = () => {
    toggleInactivityDialog();
    window.localStorage.removeItem(constants.INACTIVITY_START_PROP);
    isWarningMessageOpened = false;
    notifyOtherTabsOnInactivityStatus(constants.THRESHOLD.HIGH);
  };

  await logoffAndRedirect(callback);
}

function initializeContinuesInactivityCheck(toggleInactivityDialog) {
  setTimeout(function () {
    const inactivityStartedAt = window.localStorage.getItem(
      constants.INACTIVITY_START_PROP
    );

    if (!inactivityStartedAt) {
      initializeContinuesInactivityCheck(toggleInactivityDialog);
      return;
    }

    const inactiveTime = Date.now() - inactivityStartedAt;
    const isInactivityTimeBreached =
      inactiveTime >= constants.MAX_INACTIVITY_PERIOD;

    if (isInactivityTimeBreached) {
      logOff(toggleInactivityDialog);
      return;
    }

    const isWarningMessageTimeReached =
      inactiveTime >= constants.INACTIVITY_THRESHOLD;

    if (isWarningMessageTimeReached && !isWarningMessageOpened) {
      notifyOtherTabsOnInactivityStatus(constants.THRESHOLD.MEDIUM);
      showWarningMessage(toggleInactivityDialog);
    }

    initializeContinuesInactivityCheck(toggleInactivityDialog);
  }, constants.HEART_BEAT);
}

function showWarningMessage(toggleInactivityDialog) {
  isWarningMessageOpened = true;

  const buttons = [
    {
      id: 'logOff',
      title: translate('inactivityTimer.button.logOff'),
      type: ButtonTypes.DEFAULT,
      action: async () => await logOff(toggleInactivityDialog),
    },
    {
      id: 'continueSession',
      title: translate('inactivityTimer.button.continue'),
      type: ButtonTypes.PRIMARY,
      action: async () => {
        await extendSession(toggleInactivityDialog);
        resetOldEbankInactivityStart();
      },
    },
  ];

  toggleInactivityDialog({
    isDialogOpen: true,
    dialogTitle: translate('inactivityTimer.automaticLogOffTitle'),
    dialogText: translate('inactivityTimer.automaticLogOffExplanation'),
    dialogButtons: buttons,
  });
}

function notifyOtherTabsOnInactivityStatus(status) {
  window.localStorage.setItem(constants.THRESHOLD_PROP, status);
}

function resetOldEbankInactivityStart() {
  const sessionId =
    currentSessionId || window.sessionStorage.getItem('sessionId');
  if (!sessionId) {
    return;
  }

  const cookieName = 'clientsession' + sessionId;
  const currentTime = new Date().getTime();
  const currentTimeInMinutes = Math.round(currentTime / 1000);
  const expirationDate = new Date(currentTime + 1800000); // +30min

  document.cookie = `${cookieName}=${currentTimeInMinutes}; expires='${expirationDate.toUTCString()}'; path=/; domain=${getDomain()}`;
}

function getDomain() {
  if (process.env.NODE_ENV === 'development') {
    return 'localhost';
  }

  const domainRegex = new RegExp(/(\w+)\.(dk|lu|co\.uk|se|fi|no)/);

  return document.domain.match(domainRegex)[0];
}

function getCookie(name) {
  const match = document.cookie.match(
    RegExp('(?:^|;\\s*)' + name + '=([^;]*)')
  );
  return match && match[1];
}
